<template>
  <table-wrapper
    ref="tableWrapper"
    :title="title"
    :hideSearch="true"
    :hideTableFooter="true"
    :headers="headers"
    :page="currentPage"
    :itemsPerPage="initialItemsPerPage"
    :loading="store.isLoading"
    :allItems="store.filteredAndSorted"
    :selectedItems.sync="selected"
    :singleSelect="singleSelect"
    :showSelect="showSelect"
    :controlElements="controlElements"
    :itemClass="itemClass"
    :sort-by="sortBy"
    :sort-desc="sortDesc"
    :groupBy="groupBy"
    :groupDesc="groupDesc"
    @click:row="hasClickRowListener ? $emit('click:row', $event) : undefined"
  >
    <slot></slot>

    <template #group.header="{ groupBy, group, toggle, isOpen, items, headers, remove, isMobile, hover }">
      <slot
        name="group.header"
        :group="group"
        :groupBy="groupBy"
        :toggle="toggle"
        :isOpen="isOpen"
        :items="items"
        :headers="headers"
        :remove="remove"
        :isMobile="isMobile"
        :hover="hover"
      />
    </template>

    <template #selectActions>
      <slot v-if="selected.length" name="selectActions" />
    </template>
    <template #bodyActions>
      <slot v-if="!selected.length" name="bodyActions" />
    </template>

    <template #headerActions>
      <slot name="headerActions" />
    </template>

    <template v-for="header of headers" :slot="`item.${header.value}`" slot-scope="{ item, value }">
      <slot :name="`item.${header.value}`" :item="item" :value="value" v-if="header.value"> </slot>
    </template>

    <template #no-data>
      <slot name="onboarding" v-if="!store.filters.length && !store.search">
        <slot name="no-data">
          <latest-entries-card-empty :small="false" class="mt-2 mb-4" />
        </slot>
      </slot>
      <slot name="no-data" v-else>
        <latest-entries-card-empty :small="false" class="mt-2 mb-4" />
      </slot>
    </template>

    <template #search>
      <filter-card-pagination
        style="min-width: 50%"
        class="ma-n4"
        :filterList="store.filterOptions"
        :filter="store.filters"
        :search="store.search"
        :hideDivider="true"
        :predefinedFilter="predefinedFilter"
        :showAs="'side-card'"
        :quickFilters="quickFilters"
        @update:search="debounceSetSearch"
        @update:filter="setFilter"
        @confirmSearch="debounceFetchFirstPage({ ...baseQuery })"
        @confirmFilter="debounceFetchFirstPage({ ...baseQuery })"
      />
    </template>

    <template #footer>
      <v-progress-linear
        color="grey"
        :height="2"
        :value="((initialItemsPerPage * currentPage) / totalElements) * 100"
        :buffer-value="(filteredCount / totalElements) * 100"
      />
      <div style="display: flex; align-items: center; justify-content: center">
        <v-tooltip v-if="isCsvExport" top>
          <template #activator="{ on, attrs }">
            <v-btn @click="download" icon :elevation="0" class="mr-1" v-bind="attrs" v-on="on">
              <v-icon> mdi-download </v-icon>
            </v-btn>
          </template>
          <span>{{ $t("components.partner.PartnerReportList.exportTooltip") }}</span>
        </v-tooltip>

        <v-btn v-if="!isLoadingAll" @click="startLoadAll" text>
          {{ $t("components.partner.PartnerReportList.loadAll") }} <v-icon>mdi-play</v-icon>
        </v-btn>
        <v-btn v-else @click="isInterrupted = true" text>
          # {{ filteredCount }}

          {{ $t("components.partner.PartnerReportList.loadAllInterrupt") }} <v-icon>mdi-pause</v-icon>
        </v-btn>

        <v-spacer />

        <v-select
          dense
          v-model="initialItemsPerPage"
          :items="itemsPerPageOptions"
          style="max-width: 80px"
          class="mr-2 mt-2 mb-n2"
          @change="onItemsPerPageChanged"
          data-test-items-per-page
        >
          <template #item="{ item }">
            <small>{{ item }}</small>
          </template>
        </v-select>

        <small>
          {{ initialItemsPerPage * (currentPage - 1) + 1 }} -
          {{ initialItemsPerPage * currentPage > totalElements ? totalElements : initialItemsPerPage * currentPage }} of
          {{ totalElements }}
        </small>

        <v-btn icon :disabled="currentPage === 1" :loading="isLoadingMore" @click="setPreviousPage" data-test-page-back>
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn
          icon
          :disabled="totalElements / initialItemsPerPage <= currentPage"
          :loading="isLoadingMore"
          @click="fetchNextPage"
          data-test-page-next
        >
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </template>
  </table-wrapper>
</template>

<script lang="ts">
import LatestEntriesCardEmpty from "@/components/cards/LatestEntriesCardEmpty.vue";
import FilterCardPagination from "@/components/filter/FilterCardPagination.vue";
import Debug from "@/components/utility/Debug.vue";
import TableWrapper, { ITableWrapperHeader, IControlElements } from "@/components/utility/TableWrapper.vue";
import TheLayoutPortal from "@/layouts/TheLayoutPortal.vue";
import { IPaginationParams } from "@/store/modules/base-pagination.store";
import { PaginatedBaseStore } from "@/store/paginated-base.store";
import { Component, Prop } from "vue-property-decorator";
import { debounce } from "debounce";
import { handleError } from "@/lib/utility/handleError";
import { PageDefaults } from "@/lib/utility/data/page-data-provider.abstract";
import PermissionMixin from "@/mixins/PermissionMixin.vue";
import { downloadAsXlsx, getTranslationsFromFilterable } from "@/lib/download-as-xlsx";
import { FeatureModule } from "@/store/modules/feature.store";
import { IPageFilterElement } from "@/models/page-filter-element.entity";
import { PredefinedFilterType } from "@/views/event/filter/event.filter";
import {
  getArrayedNestedPath,
  getNestedObjectValues,
  propertiesToArray,
  setNestedObjectValues
} from "@/lib/objectPath-helper";
import { simpleExcelDownload } from "../../../lib/download-as-xlsx";

export interface IDownloadConfig<T> {
  transform: (item: T) => Record<string, any>;
  headers: Map<string, string>;
}

@Component({
  components: {
    TheLayoutPortal,
    TableWrapper,
    LatestEntriesCardEmpty,
    FilterCardPagination,
    Debug
  }
})
export default class PaginationTableNew<
  Type extends object,
  PaginationParamsType extends IPaginationParams
> extends PermissionMixin {
  /**
   * SETTING 4 TESTING Purposes
   *
   * if set to false we do not load the next page if we have enough elements
   * if set to true we always load the next page (unless we go to previous page (unless unless the previous page is the first page))
   */
  readonly LOAD_ONLY_WHEN_NECESSARY = true;

  /**
   * Store that contains the paginated data
   */
  @Prop()
  store!: PaginatedBaseStore<Type, PaginationParamsType>;

  /**
   * Title of the table
   */
  @Prop()
  title!: string;

  /**
   * Headers for the table
   */
  @Prop()
  headers!: ITableWrapperHeader[];

  /**
   * Query parametes that should be sent with each pagination request.
   *
   * @example { partnerId: "partnerId" }
   * @example { suserId: "userId" }
   */
  @Prop({
    default: () => {
      return {};
    }
  })
  baseQuery!: PaginationParamsType;

  /**
   * Predefined filters that should be shown in the filter card as suggestions
   */
  @Prop({ default: () => [] })
  predefinedFilter!: PredefinedFilterType[];

  @Prop({ default: () => [] })
  quickFilters!: PredefinedFilterType[];

  @Prop({ default: false })
  singleSelect?: boolean;

  @Prop({ default: false })
  showSelect?: boolean;

  @Prop({ default: () => [] })
  selectedItems!: Record<string, any>[];

  @Prop()
  controlElements?: IControlElements[];

  @Prop({})
  groupBy?: string;

  @Prop({ default: false })
  groupDesc?: boolean;

  @Prop()
  itemClass?: string | Function;

  @Prop()
  sortBy?: string;

  @Prop({ default: true })
  sortDesc?: boolean;

  @Prop()
  itemsPerPage?: number;

  get tableSortDesc() {
    return (this.$refs.tableWrapper as TableWrapper)?.tableSortDesc;
  }

  get tableSortBy() {
    return (this.$refs.tableWrapper as TableWrapper)?.tableSortBy;
  }

  /**
   * Downloads only the visible table columns as displayed, using header translations instead of keys and preserving column order.
   */
  @Prop({ default: false, required: false })
  isDownloadAsDisplayed?: boolean;

  @Prop({ default: undefined, required: false })
  downloadConfig?: IDownloadConfig<Type>;

  get isCsvExport() {
    return FeatureModule.isCsvExport;
  }

  get hasClickRowListener() {
    return Boolean(this.$listeners && this.$listeners["click:row"]);
  }

  initialItemsPerPage = this.itemsPerPage ?? this.store.itemsPerPage;

  isLoadingMore = false;

  isInterrupted = false;

  isLoadingAll = false;

  /**
   * Current page
   */
  currentPage = 1;

  get selected() {
    return this.selectedItems;
  }

  set selected(selectedItems: Record<string, any>[]) {
    this.$emit("update:selectedItems", selectedItems);
  }

  /**
   * Cached count of filtered items to avoid calling store.filtered multiple times per render.
   */
  get filteredCount() {
    return this.store.filtered.length;
  }

  /**
   * Total number of elements available
   */
  get totalElements() {
    const count = this.filteredCount;
    if (count > this.store.totalItems) {
      return count;
    }

    return this.store.totalItems;
  }

  /**
   * Options for the items per page select
   */
  get itemsPerPageOptions() {
    return this.store.pageSizes.concat(PageDefaults.MAX_ITEMS_PER_PAGE_ADMINS);
  }

  /**
   * Fetch the first page when the component is mounted
   */
  async mounted() {
    await this.onItemsPerPageChanged(this.initialItemsPerPage);
  }

  download() {
    let list = this.selected;
    if (!list.length) {
      list = this.store.filteredAndSorted;
    }

    if (this.downloadConfig) {
      const { transform, headers } = this.downloadConfig;

      const transformedItemList = (list as Type[]).map((item) => transform(item));

      return simpleExcelDownload(transformedItemList, headers);
    }

    if (this.isDownloadAsDisplayed) {
      list = list.map((listItem) => {
        const mapped: Record<string, any> = {};
        this.headers.forEach((header) => {
          const key = header.value;

          if (header.mapping) {
            const value = header.mapping(listItem);
            setNestedObjectValues(mapped, key, value);
            return;
          }

          const value = getNestedObjectValues(listItem, key);

          // SSKIP ACTION ROW
          if (key === "controls" && !value) {
            return;
          }
          // Only process real objects: not null, not arrays, not primitives
          if (typeof value !== "object" || value === null || Array.isArray(value)) {
            setNestedObjectValues(mapped, key, value);
            return;
          }
          const subPaths = propertiesToArray(value);
          for (const subPath of subPaths) {
            const arrayedPath = getArrayedNestedPath(key, subPath);
            setNestedObjectValues(mapped, key + arrayedPath, getNestedObjectValues(value, subPath));
          }
        });
        return mapped;
      });
    }

    const headerTranslations = getTranslationsFromFilterable(this.store.filterOptions);
    for (const header of this.headers) {
      if (!headerTranslations.get(header.value)) {
        headerTranslations.set(header.value, header.text);
      }
    }

    downloadAsXlsx(list, headerTranslations, !this.isDownloadAsDisplayed, !this.isDownloadAsDisplayed);
  }

  /**
   * Set the filter in the store without making backend call
   */
  async setFilter(filter: IPageFilterElement[]) {
    this.$log.debug("setFilterAndFetch", filter);
    this.store.filters.splice(0, this.store.filters.length, ...filter);
  }

  /**
   * Set the search in the store without making backend call
   */
  private setSearch(search: string) {
    this.$log.debug("setSearchAndFetch", search);
    this.store.setSearch(search);
  }

  /**
   * add debounce to setSearch
   */
  debounceSetSearch = debounce(this.setSearch, 500);

  /**
   * add debounce to fetchFirstPage. used when search or filter is confirmed

   */
  debounceFetchFirstPage = debounce(this.store.fetchFirstPage, 500);

  /**
   * Fetch the next page
   */
  async fetchNextPage() {
    try {
      this.isLoadingMore = true;
      const nextPage = this.currentPage + 1;
      if (this.LOAD_ONLY_WHEN_NECESSARY) {
        const isNeccessary = this.filteredCount < this.initialItemsPerPage * nextPage;
        // are there less elements than we need? Load more
        if (isNeccessary) {
          await this.store.fetchNextPage({ ...this.baseQuery });
        }
      } else {
        await this.store.fetchNextPage({ ...this.baseQuery });
      }

      this.currentPage = nextPage;
    } catch (e) {
      handleError(e);
    } finally {
      this.isLoadingMore = false;
    }
  }

  /**
   * Set the previous page
   */
  async setPreviousPage() {
    this.currentPage--;

    if (this.currentPage === 1) {
      try {
        this.isLoadingMore = true;
        await this.store.fetchFirstPage({ ...this.baseQuery });
      } catch (e) {
        handleError(e);
      } finally {
        this.isLoadingMore = false;
      }
    }
  }

  /**
   * When the items per page is changed, we might need to fetch the first page again if it is not complete yet
   * also makes sure that the items per page fetched from the backedn is not less than the default items per page
   */
  async onItemsPerPageChanged(newItemsPerPage: number) {
    if (newItemsPerPage < PageDefaults.DEFAULT_ITEMS_PER_PAGE) newItemsPerPage = PageDefaults.DEFAULT_ITEMS_PER_PAGE;

    try {
      this.isLoadingMore = true;
      this.store.setItemsPerPage(newItemsPerPage);
      const isNeccessary = this.filteredCount < newItemsPerPage * 1;

      if (!this.LOAD_ONLY_WHEN_NECESSARY) {
        await this.store.fetchFirstPage({ ...this.baseQuery });
      } else if (isNeccessary) {
        await this.store.fetchFirstPage({ ...this.baseQuery });
      }

      this.currentPage = 1;
    } catch (e) {
      handleError(e);
    } finally {
      this.isLoadingMore = false;
    }
  }

  async startLoadAll() {
    this.isInterrupted = false;
    this.isLoadingAll = true;
    this.itemsPerPage = PageDefaults.MAX_ITEMS_PER_PAGE_ADMINS;
    this.onItemsPerPageChanged(PageDefaults.MAX_ITEMS_PER_PAGE_ADMINS);
    await this.loadAll();
    this.isLoadingAll = false;
  }

  /**
   * Load EVERYTHING in small pages until we have everything
   */
  async loadAll() {
    if (this.isInterrupted) return;
    this.$log.debug("loadAll");

    try {
      this.isLoadingMore = true;
      await this.store.fetchNextPage({ ...this.baseQuery });

      if (this.filteredCount < this.store.totalItems) {
        await this.loadAll();
      }
    } catch (e) {
      handleError(e);
    } finally {
      this.isLoadingMore = false;
    }
  }
}
</script>
